import { ReviewReply } from "@/services/bedrock";
import React from "react";
import { printDate } from "@/utils";

interface ReviewReplyItemProps {
  reply: ReviewReply;
  /** When false, omit nested reply threads. Defaults to true. */
  showNestedReplies?: boolean;
}

/** Prefer API top-level replies; drop any that also appear nested under another reply. */
export function getTopLevelReviewReplies(replies: ReviewReply[] | null | undefined): ReviewReply[] {
  if (!replies?.length) return [];

  const nestedGuids = new Set<string>();
  const collectNested = (list: ReviewReply[]) => {
    for (const reply of list) {
      for (const child of reply.reviewReplies ?? []) {
        if (child.guid) nestedGuids.add(child.guid);
        collectNested([child]);
      }
    }
  };
  collectNested(replies);

  if (nestedGuids.size === 0) return replies;
  return replies.filter(reply => !reply.guid || !nestedGuids.has(reply.guid));
}

export function countReviewReplies(replies: ReviewReply[] | null | undefined): number {
  if (!replies?.length) return 0;
  return replies.reduce((sum, reply) => sum + 1 + countReviewReplies(reply.reviewReplies), 0);
}

export function ReviewReplyItem({ reply, showNestedReplies = true }: ReviewReplyItemProps) {
  const authorName = reply.displayName || "Response";
  const nestedReplies = showNestedReplies ? reply.reviewReplies : undefined;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1 rounded-md bg-slate-50 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800">{authorName}</span>
          {reply.publishedAt && <span className="text-xs text-slate-500">{printDate(reply.publishedAt)}</span>}
        </div>
        {reply.content && (
          <div
            className="text-sm text-slate-700 whitespace-pre-wrap [&_span]:font-semibold"
            dangerouslySetInnerHTML={{ __html: reply.content }}
          />
        )}
      </div>
      {nestedReplies && nestedReplies.length > 0 && (
        <div className="ml-4 flex flex-col gap-2 border-l border-slate-200 pl-3">
          {nestedReplies.map((nestedReply, idx) => (
            <ReviewReplyItem key={nestedReply.guid ?? idx} reply={nestedReply} />
          ))}
        </div>
      )}
    </div>
  );
}
